/**
 * User-facing copy for web live chat API errors.
 */

/** Shown when the agent leaves or the chat session ends. */
export const LIVE_CHAT_SESSION_ENDED_MESSAGE =
  'This chat has ended. Start a new conversation.';

const BY_CODE: Record<string, string> = {
  CHAT_ENDED: LIVE_CHAT_SESSION_ENDED_MESSAGE,
  CHAT_ALREADY_TAKEN: 'This chat was already accepted by another agent.',
  SESSION_CLOSED: LIVE_CHAT_SESSION_ENDED_MESSAGE,
  FEATURE_DISABLED:
    'Live chat is not available right now. Please try again later or contact support.',
  DYNAMO_SESSION_TABLE:
    'We could not save your chat session. Please try again or contact support.',
  TRANSCRIPT_NOT_FOUND: 'No conversation record is available yet. It may still be processing.',
  NO_CONTACT: 'We could not find that conversation.'
};

/** If the string looks like a stack trace or raw SDK noise, treat as unreadable. */
function looksTechnical(s: string): boolean {
  if (!s || s.length > 320) return true;
  if (/^\s*at\s+/.test(s) || /Traceback|undefined is not|is not a function|TypeError:|ReferenceError:/i.test(s)) {
    return true;
  }
  if (/StartChatContact|CreateParticipantConnection|DisconnectParticipant|SendMessageCommand|ResourceNotFoundException|NetworkingError/i.test(s)) {
    return true;
  }
  return false;
}

/** Normalize known backend `message` text (part-d connectLiveChat). */
function mapRawMessage(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const lower = t.toLowerCase();

  if (lower.includes('embed_token is required')) {
    return 'Chat could not start. Refresh the page or contact your administrator.';
  }
  if (lower.includes('invalid embed token')) {
    return 'This chat link is not valid. Ask your administrator for an updated link.';
  }
  if (lower.includes('could not verify embed token')) {
    return 'We could not verify your chat link. Please try again in a moment.';
  }
  if (lower.includes('amazon connect is not configured')) {
    return BY_CODE['CONNECT_NOT_CONFIGURED'];
  }
  if (lower.includes('instanceid and contactflowid must be uuids')) {
    return 'Chat could not start. Refresh the page or contact your administrator.';
  }
  if (lower.includes('returned no participant token')) {
    return 'We could not start the chat. Please try again.';
  }
  if (lower.includes('participant connection failed')) {
    return 'We could not connect to chat. Please check your internet and try again.';
  }
  if (lower.includes('missing url or credentials')) {
    return 'We could not connect to chat. Please try again.';
  }
  if (lower.includes('connectchatsession is missing') || lower.includes('dynamo_session_table')) {
    return BY_CODE['DYNAMO_SESSION_TABLE'];
  }
  if (lower.includes('unknown chat session')) {
    return 'That chat session no longer exists. Start a new chat.';
  }
  if (lower.includes('session does not belong')) {
    return 'This action is not allowed for your session. Start a new chat.';
  }
  if (lower.includes('this chat has ended')) {
    return BY_CODE['CHAT_ENDED'];
  }
  if (lower.includes('embed_token') && lower.includes('sessionid') && lower.includes('required')) {
    return 'We could not connect to chat. Please try again.';
  }
  if (lower === 'content is required' || lower.includes('content is required')) {
    return 'Please enter a message before sending.';
  }
  if (lower.includes('filebase64 is required')) {
    return 'Please choose a file to upload.';
  }
  if (lower.includes('invalid base64')) {
    return 'That file could not be read. Try another file.';
  }
  if (lower.includes('attachment too large')) {
    return 'That file is too large. Please send a smaller file (under 4 MB).';
  }
  if (lower.includes('no download url returned') || lower.includes('download url failed')) {
    return 'We could not prepare that file for download. Please try again.';
  }
  if (lower.includes('no transcript object found') || lower.includes('transcript_not_found')) {
    return BY_CODE['TRANSCRIPT_NOT_FOUND'];
  }
  if (lower.includes('session has no contactid')) {
    return BY_CODE['NO_CONTACT'];
  }
  if (lower.includes('failed to load transcript')) {
    return 'We could not load the conversation record. Please try again later.';
  }
  if (lower.includes('client_id and email are required')) {
    return 'Missing information to look up chats. Please contact support.';
  }
  if (
    lower.includes('access denied') ||
    lower.includes('access is denied') ||
    lower === 'forbidden' ||
    lower.includes('not authorized') ||
    lower.includes('unauthorized')
  ) {
    return 'Try starting a new chat, or contact your administrator if you need help.';
  }

  if (looksTechnical(t)) return null;
  return t;
}

function httpStatusFallback(status: number | undefined, fallback: string): string {
  if (status === 0 || status == null) {
    return 'You appear to be offline. Check your connection and try again.';
  }
  if (status === 400) return 'We could not process that request. Please try again.';
  if (status === 401) {
    return 'Your session has expired. Refresh the page or contact support.';
  }
  if (status === 403) {
    return 'You may not have permission to use live chat. Try starting a new chat or contact support.';
  }
  if (status === 404) return 'That chat or file could not be found.';
  if (status === 410) return BY_CODE['CHAT_ENDED'];
  if (status === 413) return 'That file is too large to send.';
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
  if (status === 503) return 'Live chat is temporarily unavailable. Please try again later.';
  if (status != null && status >= 500) {
    return 'Something went wrong on our side. Please try again in a few minutes.';
  }
  return fallback;
}

/**
 * Friendly message from an Angular Http failure (or similar `{ error: { message, code } }`).
 */
export function liveChatUserMessage(err: unknown, fallback: string): string {
  const e = err as {
    status?: number;
    message?: string;
    error?: string | { message?: string; code?: string; phase?: string };
  };

  const errBody =
    e?.error != null && typeof e.error === 'object' && !Array.isArray(e.error)
      ? (e.error as { message?: string; code?: string })
      : null;

  const code = errBody?.code;
  if (code && typeof code === 'string' && BY_CODE[code]) {
    return BY_CODE[code];
  }

  const raw =
    (typeof errBody?.message === 'string' && errBody.message) ||
    (typeof e?.error === 'string' && e.error) ||
    (typeof e?.message === 'string' && e.message) ||
    '';

  const mapped = mapRawMessage(raw);
  if (mapped) return mapped;

  if (raw && !looksTechnical(raw) && raw.length <= 200) {
    return raw;
  }

  return httpStatusFallback(e?.status, fallback);
}

/**
 * Friendly message from a successful HTTP body that still indicates failure (e.g. start chat without sessionId).
 */
export function liveChatResponseMessage(
  res:
    | { message?: string; code?: string; phase?: string; ok?: boolean; url?: string }
    | null
    | undefined,
  fallback: string
): string {
  if (!res) return fallback;
  if (res.code && BY_CODE[res.code]) return BY_CODE[res.code];
  const mapped = res.message ? mapRawMessage(res.message) : null;
  if (mapped) return mapped;
  if (res.phase && typeof res.message === 'string' && res.message) {
    const m = mapRawMessage(res.message);
    if (m) return m;
    return 'We could not connect you to chat. Please try again.';
  }
  if (typeof res.message === 'string' && res.message.trim() && !looksTechnical(res.message)) {
    return res.message.trim();
  }
  return fallback;
}
